import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Articolo } from '../../../_models/Articolo';
import {
  AnteprimaImpattoOrdiniArticolo, ArticoliService, PropagaAiOrdiniArticoloRequest,
} from '../../../_services/articoli.service';
import { OrganizzazioniService } from '../../../_services/organizzazioni.service';
import { SaveMessage } from '../../../_helpers/save-message';
import { AuthenticationService } from '../../../_services/authentication.service';
import { constants } from '../../../_helpers/constants';
import {
  DiffPropagazioneArticolo, calcolaDiffArticolo, haModifichePropagabiliArticolo,
} from '../../../_helpers/articolo-propagazione';
import {
  PropagaOrdiniArticoloModalComponent,
} from '../propaga-ordini-articolo-modal/propaga-ordini-articolo-modal.component';

export const emptyArticolo = (): Articolo => ({
  organizzazioneId: 0,
  id: 0,
  codArt: '',
  varianteArt: '',
  desArt: '',
  desEstesaArt: '',
  tipoArt: 1,
  umMag: 'N',
  magNumDecimaliQtaUm: 2,
  umMag2: '',
  giacPerLotti: 0,
  statoArt: 0
} as Articolo);

@Component({
  selector: 'app-articoli-prodotti-create',
  templateUrl: './articoli-prodotti-create.component.html',
  styleUrls: ['./articoli-prodotti-create.component.scss'],
  standalone: false
})
export class ArticoliProdottiCreateComponent implements OnInit {
  @Input() articolo: Articolo;
  @Input() isNew = true;

  loading = false;

  // Feature 121 (issue #3372) — stato di apertura della scheda, copia PROFONDA presa in ngOnInit:
  // è il termine di paragone del diff, e va preso prima di qualunque modifica dell'operatore.
  private snapshot: Articolo;

  tipiArticolo = [
    { label: 'Prodotto finito', value: 1 },
    { label: 'Semilavorato',    value: 2 },
    { label: 'Materia prima',   value: 3 },
    { label: 'Servizio',        value: 4 }
  ];

  statiArticolo = [
    { label: 'Attivo',          value: 0 },
    { label: 'In esaurimento',  value: 1 },
    { label: 'Sospeso',         value: 2 },
    { label: 'Obsoleto',        value: 3 }
  ];

  constructor(
    private articoliService: ArticoliService,
    private organizzazioniService: OrganizzazioniService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage,
    private authService: AuthenticationService
  ) {}

  ngOnInit() {
    if (!this.articolo) {
      this.articolo = emptyArticolo();
      this.articolo.organizzazioneId = this.organizzazioniService.organizzazioneId;
    }
    this.snapshot = JSON.parse(JSON.stringify(this.articolo)) as Articolo;
  }

  async confirm() {
    if (!this.articolo.codArt?.trim() || !this.articolo.desArt?.trim()) {
      this.saveMessage.messageError('Codice articolo e descrizione sono obbligatori.');
      return;
    }
    // Il diff va calcolato PRIMA di salvare: dopo, snapshot e stato corrente coinciderebbero.
    const diff = calcolaDiffArticolo(this.snapshot, this.articolo);
    this.loading = true;
    try {
      const saved = await this.salvaArticolo();
      await this.chiediPropagazione(diff);
      this.modalCtrl.dismiss(saved, 'confirm');
    } catch {
      this.saveMessage.messageError('Ci sono problemi con il salvataggio.');
    } finally {
      this.loading = false;
    }
  }

  private async salvaArticolo(): Promise<Articolo> {
    const saved = this.isNew
      ? await this.articoliService.createArticoloAsync(this.articolo)
      : await this.articoliService.updateArticoloAsync(this.articolo);
    this.saveMessage.messageInfo(
      this.isNew ? `Articolo ${saved.codArt} creato.` : `Articolo ${saved.codArt} aggiornato.`);
    return saved;
  }

  // Feature 121 (issue #3372) — dialogo di propagazione alle righe d'ordine, DOPO il salvataggio.
  // Qualunque esito qui lascia l'articolo SALVATO: la propagazione è un'operazione a sé (FR-003).
  private async chiediPropagazione(diff: DiffPropagazioneArticolo) {
    if (!this.propagazioneProponibile(diff)) return;

    const anteprima = await this.anteprimaImpatto();
    // Zero righe TOTALI ⇒ nessun dialogo: non si chiede nulla per un'azione che non farebbe niente
    // (FR-016). ⚠️ Il conteggio di riferimento è quello sui TOTALI, mai `righeInCorso`: un articolo
    // con righe tutte consegnate ha comunque un'operazione legittima nell'ambito "tutte le righe".
    if (anteprima.disponibile && (anteprima.articolo?.righeTotali ?? 0) === 0) return;

    const request = await this.apriDialogo(diff, anteprima);
    if (!request) return;   // "Non aggiornare": nessuna riga cambia, l'articolo resta salvato.
    await this.avviaPropagazione(request);
  }

  private propagazioneProponibile(diff: DiffPropagazioneArticolo): boolean {
    // Nessun dialogo in creazione: nessuna riga d'ordine può ancora referenziare l'articolo.
    if (this.isNew) return false;
    // Il gate di ruolo replica quello server-side (FR-040). Il SALVATAGGIO non ne dipende mai
    // (FR-041): chi non ha ARTICLE ha già salvato, semplicemente non vede il dialogo.
    if (!this.authService.isInRole(constants.ROLE_ART)) return false;
    if (this.articolo.id <= 0) return false;   // FR-042: mai su un articolo inesistente
    return haModifichePropagabiliArticolo(diff);
  }

  private async anteprimaImpatto(): Promise<AnteprimaImpattoOrdiniArticolo> {
    try {
      return await this.articoliService.anteprimaImpattoOrdiniArticoloAsync(
        this.articolo.organizzazioneId, this.articolo.id);
    } catch {
      // Fail-open (FR-012, FR-018): il dialogo si apre comunque, senza numeri.
      return { disponibile: false, varianti: [] };
    }
  }

  private async apriDialogo(diff: DiffPropagazioneArticolo, anteprima: AnteprimaImpattoOrdiniArticolo) {
    const modal = await this.modalCtrl.create({
      component: PropagaOrdiniArticoloModalComponent,
      // Classe dedicata (global.scss), già introdotta dalla feature 120: questo dialogo si apre
      // sopra la scheda articolo e deve leggersi come un livello sovrapposto.
      cssClass: 'propaga-ordini-modal',
      componentProps: {
        diff, anteprima,
        orgId: this.articolo.organizzazioneId,
        articoloId: this.articolo.id,
      },
    });
    await modal.present();
    // onDidDismiss, NON onWillDismiss: quest'ultima si risolve quando il dialogo COMINCIA a
    // chiudersi, con l'animazione di uscita ancora in corso. Il chiamante fa poi la POST e chiude
    // questa scheda con `modalCtrl.dismiss()`, che senza id chiude l'overlay in CIMA alla pila: se
    // il dialogo è ancora lì, si prende lui il dismiss e la scheda articolo resta aperta.
    const { data, role } = await modal.onDidDismiss();
    return role === 'confirm' ? (data as PropagaAiOrdiniArticoloRequest) : null;
  }

  // FR-043 — un fallimento qui deve essere ESPLICITO: il silenzio farebbe credere all'operatore che
  // le righe siano allineate quando non lo sono.
  private async avviaPropagazione(request: PropagaAiOrdiniArticoloRequest) {
    try {
      await this.articoliService.propagaAiOrdiniArticoloAsync(request);
      this.saveMessage.messageInfo('Aggiornamento delle righe d\'ordine collegate avviato.');
    } catch (errore) {
      this.saveMessage.messageError(this.messaggioDiErrore(errore));
    }
  }

  // Tre casi distinti (FR-043). ⚠️ Il terzo NON riusa il testo del secondo: affermerebbe il falso,
  // perché su un timeout il servizio ordini può aver già accodato il messaggio.
  private messaggioDiErrore(errore: unknown): string {
    const risposta = errore as { status?: number; error?: { avvioIncerto?: boolean } };
    if (risposta?.status === 403) {
      return 'Non hai i permessi per aggiornare gli ordini collegati. '
        + 'L\'articolo è stato comunque salvato.';
    }
    if (risposta?.error?.avvioIncerto) {
      return 'Non è stato possibile confermare l\'avvio dell\'aggiornamento degli ordini: '
        + 'riprovare non duplica nulla. L\'articolo è stato comunque salvato.';
    }
    return 'L\'aggiornamento degli ordini NON è partito: il servizio ordini non è raggiungibile. '
      + 'L\'articolo è stato comunque salvato.';
  }

  cancel = () => this.modalCtrl.dismiss(false, 'cancel');
}
